import React, { useState, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView, InfoWindow, Marker } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { FaBuilding, FaTent, FaMapPin, FaFire, FaHouseFloodWater, FaHeart, FaTruck, FaExclamation, FaPen } from 'react-icons/fa6';
import ReactDOMServer from 'react-dom/server';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import './Main.css';

// --- Responsive styles for Homepage ---

const incidentMapRenderer = {
  render: ({ count, position, markers }) => {
    try {
      const clusterType = markers[0]?.incident?.type?.toLowerCase() || 'others';
      
      const getClusterColor = (type) => {
        switch (type) {
          case 'fire': return '#e65100';
          case 'flood': return '#1976d2';
          case 'medical': return '#d32f2f';
          case 'rescue': return '#388e3c';
          default: return '#fbc02d';
        }
      };

      const clusterColor = getClusterColor(clusterType);

      const svg = `
        <svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
          {/* Outer radius circle */}
          <circle 
            cx="60"
            cy="60"
            r="58"
            fill="${clusterColor}"
            fill-opacity="0.2"
            stroke="${clusterColor}"
            stroke-width="1"
          />

          {/* Inner count circle (the "glow" one) */}
          <defs>
            <radialGradient id="grad1" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
              <stop offset="40%" stop-color="${clusterColor}" stop-opacity="0.9"/>
              <stop offset="100%" stop-color="${clusterColor}" stop-opacity="0.1"/>
            </radialGradient>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <circle cx="60" cy="60" r="24" fill="url(#grad1)" filter="url(#shadow)"/>

          {/* Count text */}
          <text x="60" y="66" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="18" font-weight="bold" style="text-shadow: 0 0 3px rgba(0,0,0,0.5);">${count}</text>
        </svg>
      `;

      return new window.google.maps.Marker({
        position,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
          scaledSize: new window.google.maps.Size(120, 120),
          anchor: new window.google.maps.Point(60, 60),
        },
        zIndex: 10,
      });
    } catch (e) {
      console.error('Error in custom cluster renderer:', e);
      return new window.google.maps.Marker({ position });
    }
  },
};

function Homepage() {
  useEffect(() => {
    document.title = "Home";
  }, []);

  const navigate = useNavigate();

  const [shelters, setShelters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: 13.1391, lng: 123.7438 }); // Legazpi City default
  const [selectedShelter, setSelectedShelter] = useState(null);

  
  const [incidents, setIncidents] = useState([]);
  const [reportsMapCenter, setReportsMapCenter] = useState({ lat: 13.1391, lng: 123.7438 }); // Legazpi City default
  const [selectedIncident, setSelectedIncident] = useState(null);

  const [activeSection, setActiveSection] = useState('home');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // State for map instances and filters
  const [reportsMap, setReportsMap] = useState(null);
  const [reportTypeFilter, setReportTypeFilter] = useState('all');
  const [evacSearch, setEvacSearch] = useState('');
  const [evacPage, setEvacPage] = useState(1);
  const [reportSearch, setReportSearch] = useState('');
  const [reportPage, setReportPage] = useState(1);
  const [shelterFilter, setShelterFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const itemsPerPage = 10;

  // --- Add after other useState declarations ---
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA',
    libraries: ['places', 'marker']
  });

  const mapContainerStyle = {
    width: '100%',
    height: '500px',
    borderRadius: '20px',
    className: 'map-responsive'
  };

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    mapId: 'YOUR_MAP_ID',
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  }), []);

  useEffect(() => {
    fetchShelters();
    fetchIncidents();

    // Real-time updates for shelters
    const sheltersChannel = supabase
      .channel('public:homepage_shelters')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'evacuation_shelters' },
        (payload) => {
          console.log('Shelter change received on homepage!', payload);
          fetchShelters();
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'temp_shelter' },
        (payload) => {
          console.log('Temporary shelter change received on homepage!', payload);
          fetchShelters();
        }
      )
      .subscribe();

    // Real-time updates for incidents
    const incidentsChannel = supabase
      .channel('public:homepage_incidents')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'incident_reports' },
        (payload) => {
          console.log('Incident change received on homepage!', payload);
          fetchIncidents();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(sheltersChannel);
      supabase.removeChannel(incidentsChannel);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        { id: 'home', offset: 0 },
        { id: 'announcements', offset: document.getElementById('announcements')?.offsetTop || 0 },
        { id: 'evacuation-map', offset: document.getElementById('evacuation-map')?.offsetTop || 0 },
        { id: 'reports-map', offset: document.getElementById('reports-map')?.offsetTop || 0 },
        { id: 'about', offset: document.getElementById('about')?.offsetTop || 0 }
      ];
      const scrollPosition = window.scrollY + 180; 

      let current = 'home';
      for (let i = 1; i < sections.length; i++) {
        if (scrollPosition >= sections[i].offset) {
          current = sections[i].id;
        }
      }

      // If near the bottom, always set to 'about'
      if ((window.innerHeight + window.scrollY) >= (document.body.offsetHeight - 100)) {
        current = 'about';
      }

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  async function fetchShelters() {
  setLoading(true);
  setError('');
  try {
    const { data: evacuationData, error: evacuationError } = await supabase
      .from('evacuation_shelters')
      .select('*')
      .neq('status', 'archived'); // Add this line to filter out archived

    const { data: temporaryData, error: temporaryError } = await supabase
      .from('temp_shelter')
      .select('*')
      .eq('status', 'approved'); // This already filters for approved only

    if (evacuationError) throw evacuationError;
    if (temporaryError) throw temporaryError;

    const evacuationCenters = (evacuationData || []).map(shelter => ({
      ...shelter,
      shelter_type: 'evacuation_center',
      shelter_name: shelter.shelter_name,
      latitude: shelter.latitude,
      longitude: shelter.longitude,
    }));
    
    const temporaryShelters = (temporaryData || []).map(shelter => ({
      ...shelter,
      shelter_type: 'temporary_shelter',
      shelter_name: shelter.house_name || shelter.address,
      latitude: shelter.latitude,
      longitude: shelter.longitude,
    }));
    
    const allShelters = [...evacuationCenters, ...temporaryShelters].filter(shelter =>
      shelter.latitude && typeof shelter.latitude === 'number' &&
      shelter.longitude && typeof shelter.longitude === 'number'
    );
    
    setShelters(allShelters);
    if (allShelters.length > 0) {
      setMapCenter({ lat: allShelters[0].latitude, lng: allShelters[0].longitude });
    }
  } catch (e) {
    console.error("Error fetching shelters:", e);
    setError('Failed to load shelters. This might be due to network issues or access restrictions.');
  } finally {
    setLoading(false);
  }
}

  async function fetchIncidents() {
    try {
      const { data, error } = await supabase
        .from('incident_reports')
        .select('*');

      if (error) throw error;

      // Filter out archived incidents
      const filtered = Array.isArray(data)
        ? data.filter(i => i.status !== 'archived')
        : [];
      setIncidents(filtered);
      const first = filtered.find(i => i.location_coords && i.location_coords.latitude && i.location_coords.longitude);
      if (first) setReportsMapCenter({ lat: first.location_coords.latitude, lng: first.location_coords.longitude });
    } catch (e) {
      console.error("Error fetching incidents:", e);
      setIncidents([]); 
    }
  }


  const getShelterMarkerIcon = (shelterType) => {
    if (shelterType === 'evacuation_center') {
      return <FaBuilding />;
    } else if (shelterType === 'temporary_shelter') {
      return <FaTent />;
    }
    return <FaMapPin />;
  };

  const getIncidentMarkerIcon = (type) => {
    let iconComponent;
    const size = 32;

    switch (type.toLowerCase()) {
      case 'fire':
        iconComponent = <FaFire color='#e65100' size={size} />;
        break;
      case 'flood':
        iconComponent = <FaHouseFloodWater color='#1976d2' size={size} />;
        break;
      case 'medical':
        iconComponent = <FaHeart color='#d32f2f' size={size} />;
        break;
      case 'rescue':
        iconComponent = <FaTruck color='#388e3c' size={size} />;
        break;
      case 'others':
        iconComponent = <FaExclamation color='#fbc02d' size={size} />;
        break;
      default:
        iconComponent = <FaMapPin color='#666' size={size} />;
        break;
    }

    const svgString = ReactDOMServer.renderToString(iconComponent);
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgString)}`;
  };

  const filteredEvacShelters = shelters
    .filter(s => s.shelter_type === 'evacuation_center')
    .filter(s => s.shelter_name.toLowerCase().includes(evacSearch.toLowerCase()));

  const paginatedEvacShelters = filteredEvacShelters.slice(
    (evacPage - 1) * itemsPerPage,
    evacPage * itemsPerPage
  );

  const totalEvacPages = Math.ceil(filteredEvacShelters.length / itemsPerPage);

  const filteredIncidents = incidents
    .filter(i => (i.location || '').toLowerCase().includes(reportSearch.toLowerCase()));

  const paginatedIncidents = filteredIncidents.slice(
    (reportPage - 1) * itemsPerPage,
    reportPage * itemsPerPage
  );

  const totalIncidentPages = Math.ceil(filteredIncidents.length / itemsPerPage);
 
  const evacTextRef = useRef(null);
  const reportsTextRef = useRef(null);
  const aboutTextRef = useRef(null);
  const announcementsTextRef = useRef(null);
  const [announcementsTextVisible, setAnnouncementsTextVisible] = useState(false);
  const [evacTextVisible, setEvacTextVisible] = useState(false);
  const [reportsTextVisible, setReportsTextVisible] = useState(false);
  const [aboutTextVisible, setAboutTextVisible] = useState(false);


  useEffect(() => {
    const handleScroll = () => {
      if (announcementsTextRef.current) {
        const rect = announcementsTextRef.current.getBoundingClientRect();
        setAnnouncementsTextVisible(rect.top < window.innerHeight - 100);
      }
      if (evacTextRef.current) {
        const rect = evacTextRef.current.getBoundingClientRect();
        setEvacTextVisible(rect.top < window.innerHeight - 100);
      }
      if (reportsTextRef.current) {
        const rect = reportsTextRef.current.getBoundingClientRect();
        setReportsTextVisible(rect.top < window.innerHeight - 100);
      }
      if (aboutTextRef.current) {
        const rect = aboutTextRef.current.getBoundingClientRect();
        setAboutTextVisible(rect.top < window.innerHeight - 100);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!reportsMap || !isLoaded || incidents.length === 0) return;

    const incidentsToDisplay = reportTypeFilter === 'all'
      ? incidents
      : incidents.filter(i => i.type.toLowerCase() === reportTypeFilter);

    const incidentsByType = {};
    incidentsToDisplay.forEach(incident => {
      const type = incident.type.toLowerCase();
      if (!incidentsByType[type]) {
        incidentsByType[type] = [];
      }
      incidentsByType[type].push(incident);
    });

    const clusterers = [];
    Object.entries(incidentsByType).forEach(([type, typeIncidents]) => {
      const markers = typeIncidents.map((incident) => {
        const marker = new window.google.maps.Marker({
          position: { lat: incident.location_coords.latitude, lng: incident.location_coords.longitude },
          icon: {
            url: getIncidentMarkerIcon(incident.type),
            scaledSize: new window.google.maps.Size(32, 32),
            anchor: new window.google.maps.Point(16, 16),
          },
          title: incident.location,
        });
        marker.incident = incident;
        marker.addListener('click', () => {
          setSelectedIncident(incident);
        });
        return marker;
      });

      const markerClusterer = new MarkerClusterer({ map: reportsMap, markers, renderer: incidentMapRenderer });
      clusterers.push(markerClusterer);
    });

    return () => {
      clusterers.forEach(clusterer => clusterer.clearMarkers());
    };
  }, [reportsMap, isLoaded, incidents, reportTypeFilter]);

  const filteredShelters = useMemo(() => {
    if (shelterFilter === 'all') return shelters;
    if (shelterFilter === 'evacuation') return shelters.filter(s => s.shelter_type === 'evacuation_center');
    if (shelterFilter === 'temporary') return shelters.filter(s => s.shelter_type === 'temporary_shelter');
    return shelters;
  }, [shelters, shelterFilter]);

  // Helper function to truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Helper function to toggle row expansion
  const toggleRowExpansion = (incidentId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(incidentId)) {
      newExpandedRows.delete(incidentId);
    } else {
      newExpandedRows.add(incidentId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Helper function to check if text should be truncated
  const shouldTruncate = (text, maxLength = 100) => {
    return text && text.length > maxLength;
  };

  // Fetch announcements (latest 5, active only)
  useEffect(() => {
    let isMounted = true;
    async function fetchAnnouncements() {
      setAnnouncementsLoading(true);
      try {
        const { data, error } = await supabase
          .from('announcements')
          .select('id, title, content, banner_img, created_at')
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(5);
        if (!isMounted) return;
        if (error) throw error;
        setAnnouncements(data || []);
      } catch (e) {
        setAnnouncements([]);
      } finally {
        setAnnouncementsLoading(false);
      }
    }
    fetchAnnouncements();
    // Real-time updates
    const channel = supabase
      .channel('public:homepage_announcements')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'announcements' }, fetchAnnouncements)
      .subscribe();
    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  // Carousel auto-advance
  useEffect(() => {
    if (announcements.length <= 1) return;
    const interval = setInterval(() => {
      setCarouselIndex(idx => (idx + 1) % announcements.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [announcements]);

  useEffect(() => {
  const handleResize = () => {
    if (window.innerWidth > 1350 && mobileNavOpen) {
      setMobileNavOpen(false);
    }
  };
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, [mobileNavOpen]);

  const goToSlide = idx => setCarouselIndex(idx);
  const prevSlide = () => setCarouselIndex(idx => (idx - 1 + announcements.length) % announcements.length);
  const nextSlide = () => setCarouselIndex(idx => (idx + 1) % announcements.length);

  const truncate = (text, n = 120) => text && text.length > n ? text.slice(0, n) + '...' : text;

  return (
    <> 
      <div
        className="sticky-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 2rem',
          width: '100%',
          height: 80,
          background: 'transparent',
          borderBottom: 'none',
          fontFamily: 'inherit',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#fff',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          boxSizing: 'border-box',
        }}
      >
        <div
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '0.8'}
          onMouseLeave={(e) => e.target.style.opacity = '1'}
          title="Go to Home"
        >
          <img src="/GabaySakunaLogo.svg" alt="GabaySakuna Logo" style={{ height: 40, marginRight: 16 }} />
          <span style={{ fontWeight: 'bold', fontSize: 24, marginRight: 40 }}>GabaySakuna</span>
        </div>

        {/* --- Desktop Navigation --- */}
        <nav
          className="main-nav"
          style={{
            display: 'flex',
            gap: 32,
            marginLeft: 10,
            alignItems: 'center'
          }}
        >
          <a
            href="#"
            onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); setActiveSection('home'); }}
            style={{
              color: activeSection === 'home' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'home' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 18,
              borderBottom: activeSection === 'home' ? '2px solid #08b1c7' : 'none',
              paddingBottom: 2
            }}
          >
            Home
          </a>
          <a
            href="#announcements"
            onClick={e => {
              e.preventDefault();
              document.getElementById('announcements')?.scrollIntoView({ behavior: 'smooth' });
              setActiveSection('announcements');
            }}
            style={{
              color: activeSection === 'announcements' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'announcements' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 18,
              borderBottom: activeSection === 'announcements' ? '2px solid #08b1c7' : 'none',
              paddingBottom: 2
            }}
          >
            Announcements
          </a>
          <a
            href="#evacuation-map"
            onClick={e => {
              e.preventDefault();
              document.getElementById('evacuation-map')?.scrollIntoView({ behavior: 'smooth' });
              setActiveSection('evacuation-map');
            }}
            style={{
              color: activeSection === 'evacuation-map' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'evacuation-map' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 18,
              borderBottom: activeSection === 'evacuation-map' ? '2px solid #08b1c7' : 'none',
              paddingBottom: 2
            }}
          >
            Evacuation Map
          </a>
          <a
            href="#reports-map"
            onClick={e => {
              e.preventDefault();
              document.getElementById('reports-map')?.scrollIntoView({ behavior: 'smooth' });
              setActiveSection('reports-map');
            }}
            style={{
              color: activeSection === 'reports-map' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'reports-map' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 18,
              borderBottom: activeSection === 'reports-map' ? '2px solid #08b1c7' : 'none',
              paddingBottom: 2
            }}
          >
            Reports Map
          </a>
          <a
            href="#about"
            onClick={e => {
              e.preventDefault();
              document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
              setActiveSection('about');
            }}
            style={{
              color: activeSection === 'about' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'about' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 18,
              borderBottom: activeSection === 'about' ? '2px solid #08b1c7' : 'none',
              paddingBottom: 2
            }}
          >
            About Us
          </a>
        </nav>

        {/* Desktop/Login/Hamburger Right-Aligned Container */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          {/* Desktop Login Button - Hidden on mobile */}
          <div className="desktop-login" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <a
              href="https://expo.dev/accounts/odysseum/projects/gabaysakuna/builds/3f5b3ab6-62ab-4478-a222-327597b817b2?fbclid=IwY2xjawLP7BdleHRuA2FlbQIxMQABHjZjNjI4x_ydH2ewxh1fml1IQsXGYCbmFjETaXB2bqVvRfgJoRVu6q_bQOCP_aem_mwuHgmQkeng3wZsafny6bQ"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 20px',
                fontSize: 16,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(8, 177, 199, 0.3)',
                transition: 'all 0.3s ease',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(8, 177, 199, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(8, 177, 199, 0.3)';
              }}
            >
              Download App
            </a>
            <button
              onClick={() => navigate('/login')}
              style={{
                background: '#fff',
                color: '#08b1c7',
                border: '1px solid #08b1c7',
                borderRadius: 8,
                padding: '10px 28px',
                fontSize: 16,
                cursor: 'pointer',
                boxShadow: '0 2px 8px #0001',
                transition: 'background 0.2s'
              }}
            >
              Login
            </button>
          </div>
          {/* Hamburger menu for mobile */}
          <button
            className="mobile-nav-toggle"
            aria-label="Open navigation menu"
            onClick={() => setMobileNavOpen(v => !v)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              fontSize: 32,
              marginLeft: 16,
              cursor: 'pointer',
              color: '#08b1c7',
              alignItems: 'center',
              justifyContent: 'center',
              height: 48,
              width: 48
            }}
          >
            <span style={{fontSize: 32, lineHeight: 1}}>&#9776;</span>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      {mobileNavOpen && (
        <div
          className="mobile-nav"
          style={{
            position: 'fixed',
            top: 80,
            left: 0,
            width: '100vw',
            background: '#fff',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            zIndex: 200,
            flexDirection: 'column',
            alignItems: 'center',
            padding: '24px 0'
          }}
        >
          <a
            href="#"
            onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); setActiveSection('home'); setMobileNavOpen(false); }}
            style={{
              color: activeSection === 'home' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'home' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 20,
              borderBottom: activeSection === 'home' ? '2px solid #08b1c7' : 'none',
              padding: '12px 0',
              width: '100%',
              textAlign: 'center'
            }}
          >
            Home
          </a>
          <a
            href="#announcements"
            onClick={e => {
              e.preventDefault();
              document.getElementById('announcements')?.scrollIntoView({ behavior: 'smooth' });
              setActiveSection('announcements');
              setMobileNavOpen(false);
            }}
            style={{
              color: activeSection === 'announcements' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'announcements' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 20,
              borderBottom: activeSection === 'announcements' ? '2px solid #08b1c7' : 'none',
              padding: '12px 0',
              width: '100%',
              textAlign: 'center'
            }}
          >
            Announcements
          </a>
          <a
            href="#evacuation-map"
            onClick={e => {
              e.preventDefault();
              document.getElementById('evacuation-map')?.scrollIntoView({ behavior: 'smooth' });
              setActiveSection('evacuation-map');
              setMobileNavOpen(false);
            }}
            style={{
              color: activeSection === 'evacuation-map' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'evacuation-map' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 20,
              borderBottom: activeSection === 'evacuation-map' ? '2px solid #08b1c7' : 'none',
              padding: '12px 0',
              width: '100%',
              textAlign: 'center'
            }}
          >
            Evacuation Map
          </a>
          <a
            href="#reports-map"
            onClick={e => {
              e.preventDefault();
              document.getElementById('reports-map')?.scrollIntoView({ behavior: 'smooth' });
              setActiveSection('reports-map');
              setMobileNavOpen(false);
            }}
            style={{
              color: activeSection === 'reports-map' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'reports-map' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 20,
              borderBottom: activeSection === 'reports-map' ? '2px solid #08b1c7' : 'none',
              padding: '12px 0',
              width: '100%',
              textAlign: 'center'
            }}
          >
            Reports Map
          </a>
          <a
            href="#about"
            onClick={e => {
              e.preventDefault();
              document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
              setActiveSection('about');
              setMobileNavOpen(false);
            }}
            style={{
              color: activeSection === 'about' ? '#08b1c7' : '#222',
              fontWeight: activeSection === 'about' ? 'bold' : 'normal',
              textDecoration: 'none',
              fontSize: 20,
              borderBottom: activeSection === 'about' ? '2px solid #08b1c7' : 'none',
              padding: '12px 0',
              width: '100%',
              textAlign: 'center'
            }}
          >
            About Us
          </a>
          {/* Mobile Download App Button */}
          <a
            href="https://expo.dev/accounts/odysseum/projects/gabaysakuna/builds/3f5b3ab6-62ab-4478-a222-327597b817b2?fbclid=IwY2xjawLP7BdleHRuA2FlbQIxMQABHjZjNjI4x_ydH2ewxh1fml1IQsXGYCbmFjETaXB2bqVvRfgJoRVu6q_bQOCP_aem_mwuHgmQkeng3wZsafny6bQ"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginTop: 16,
              background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '14px 32px',
              fontSize: 18,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(8, 177, 199, 0.3)',
              transition: 'all 0.2s ease',
              fontWeight: '600',
              width: '80%',
              maxWidth: 200,
              textAlign: 'center',
              display: 'block',
              textDecoration: 'none',
            }}
            onMouseEnter={e => {
              e.target.style.background = '#0d47a1';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(8, 177, 199, 0.4)';
            }}
            onMouseLeave={e => {
              e.target.style.background = 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(8, 177, 199, 0.3)';
            }}
          >
            Download App
          </a>
          {/* Mobile Login Button */}
          <button
            onClick={() => { navigate('/login'); setMobileNavOpen(false); }}
            style={{
              marginTop: 16,
              background: '#08b1c7',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '14px 32px',
              fontSize: 18,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(8, 177, 199, 0.3)',
              transition: 'all 0.2s ease',
              fontWeight: '600',
              width: '80%',
              maxWidth: 200
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#0d47a1';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(8, 177, 199, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#08b1c7';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(8, 177, 199, 0.3)';
            }}
          >
            Login
          </button>
        </div>
      )}
    <div style={{
      maxWidth: '100vw',
      minHeight: '100vh',
      background: 'linear-gradient(90deg, #eaf6fb 60%, #d6eef2 100%)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      {/* --- Responsive main hero section --- */}
      <div className="responsive-stack" style={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '80px',
          padding: '80px 10vw 0 10vw', 
          marginTop: 48,             
          marginBottom: 96,
          minHeight: '70vh',
          maxWidth: '1400px',
          margin: '48px auto 96px auto'
        }}>
        <div className="hero-text-container" style={{ 
          flex: 1, 
          maxWidth: 600,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}> 
          <div className="hero-title" style={{ 
            fontWeight: '800', 
            fontSize: '3.5rem', 
            lineHeight: 1.2, 
            marginBottom: 32,
            padding: '8px 0',
            background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textRendering: 'optimizeLegibility',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale'
          }}>
            <span style={{ color: '#08b1c7'}}>Gabay Sakuna:</span><br />
            Guiding Communities, Saving Lives
          </div>
          <div className="hero-description" style={{ 
            fontSize: '1.40rem', 
            color: 'rgb(10, 53, 119)', 
            marginBottom: 40,
            lineHeight: 1.7,
            fontWeight: 400
          }}>
            GabaySakuna is your all-in-one disaster management platform designed to enhance community safety and resilience. With real-time updates, interactive maps, and a volunteer-driven response system, we empower communities to act swiftly during emergencies.
          </div>
        </div>
        <div className="hero-image-container" style={{ 
          flex: 1,
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: 0,
          paddingLeft: '40px'
        }}>
          <img
            className="responsive-image"
            src="/hero-image.svg"
            alt="GabaySakuna Hero Illustration"
            style={{ 
              width: '100%',
              maxWidth: 520,
              height: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 8px 24px rgba(8, 177, 199, 0.15))',
              transition: 'transform 0.3s ease'
            }} 
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          />
        </div>
      </div> {/* End of hero section */}

      {/* Announcements Carousel Section */}
      <div id="announcements" 
        className="responsive-padding section-container" 
        style={{
        display: 'flex',
        justifyContent: 'center',
        margin: '0 0 64px 0',
        padding: '32px 0',
        scrollMarginTop: 88,
        background: 'linear-gradient(135deg, #f8fcfd 0%, #e8f4f8 100%)', 
        borderTop: '1px solid rgba(8, 177, 199, 0.1)',
        borderBottom: '1px solid rgba(8, 177, 199, 0.1)'
      }}>
          <div className="responsive-stack" style={{
            width: '100%',
            maxWidth: 1400,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 48,
            justifyContent: 'center',
            margin: 32,
          }}>
          <div
            ref={announcementsTextRef}
            className={`announcements-text-section${announcementsTextVisible ? ' visible' : ''}`}
          >
            <div className="responsive-section-title" style={{ 
              fontWeight: '800', 
              fontSize: '2.5rem', 
              color: '#08b1c7', 
              marginBottom: '1em', 
              letterSpacing: 0.5,
              background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Announcements
            </div>
            <div className="responsive-section-subtitle" style={{ 
                fontSize: '1.30rem', 
                color: 'rgb(10, 53, 119)', 
                marginBottom: 24,
                lineHeight: 1.7,
                fontWeight: 400
              }}>
              Stay updated with the latest news and announcements from GabaySakuna. Our announcements section provides real-time updates on disaster management, community safety, and platform features.
            </div>
          </div>
          {announcementsLoading ? (
            <div style={{ padding: 40, color: '#08b1c7', fontWeight: 500, fontSize: 18 }}>Loading announcements...</div>
          ) : announcements.length === 0 ? (
            <div style={{ padding: 40, color: '#7f8c8d', fontWeight: 500, fontSize: 18 }}>No announcements at this time.</div>
          ) : (
            <div style={{ width: '100%', maxWidth: 900, position: 'relative', minHeight: 340, maxHeight : 420 }}>
              {/* Carousel Slide */}
              {announcements.map((a, idx) => (
                idx === carouselIndex && (
                  <div key={a.id} style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-start',
                    minHeight: 340,
                    height: '40vw',
                    maxHeight: 420,
                    width: '100%',
                    borderRadius: 24,
                    overflow: 'hidden',
                    boxShadow: '0 4px 24px rgba(8, 177, 199, 0.10)',
                    margin: '0 auto',
                    cursor: 'pointer',
                    background: a.banner_img
                      ? `url(${a.banner_img}) center center/cover no-repeat`
                      : 'linear-gradient(135deg, #eaf6fb 0%, #d6eef2 100%)',
                    transition: 'box-shadow 0.2s',
                    marginBottom: 0
                  }}>
                    {/* Overlay */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: a.banner_img
                        ? 'linear-gradient(0deg, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.25) 100%)'
                        : 'rgba(8,177,199,0.08)',
                      zIndex: 1
                    }} />
                    {/* Fallback emoji if no image */}
                    {!a.banner_img && (
                      <div style={{
                        position: 'absolute',
                        left: 32,
                        top: 32,
                        fontSize: 64,
                        color: '#08b1c7',
                        zIndex: 2,
                        filter: 'drop-shadow(0 2px 8px #0006)'
                      }}>📢</div>
                    )}
                    {/* Content */}
                    <div style={{
                      position: 'relative',
                      zIndex: 2,
                      padding: '40px 48px 36px 48px',
                      maxWidth: 600,
                      width: '100%',
                      color: '#fff',
                      textAlign: 'left',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      textShadow: '0 2px 8px rgba(0,0,0,0.35)',
                      background: 'none',
                    }}>
                      <div style={{ fontWeight: 800, fontSize: 32, marginBottom: 12, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
                      <div style={{ fontSize: 18, marginBottom: 18, lineHeight: 1.5, maxHeight: 72, overflow: 'hidden', textOverflow: 'ellipsis' }}>{truncate(a.content, 180)}</div>
                      <div style={{ fontSize: 15, color: '#e0e0e0', fontWeight: 500 }}>{new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                )
              ))}
              {/* Carousel Controls */}
              {announcements.length > 1 && (
                <>
                  {/* Left Arrow */}
                  <button onClick={e => { e.stopPropagation(); prevSlide(); }} aria-label="Previous" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(8,177,199,0.22)', border: 'none', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#fff', cursor: 'pointer', zIndex: 3, boxShadow: '0 2px 8px #0003', backdropFilter: 'blur(2px)' }}>&#8592;</button>
                  {/* Right Arrow */}
                  <button onClick={e => { e.stopPropagation(); nextSlide(); }} aria-label="Next" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(8,177,199,0.22)', border: 'none', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#fff', cursor: 'pointer', zIndex: 3, boxShadow: '0 2px 8px #0003', backdropFilter: 'blur(2px)' }}>&#8594;</button>
                  {/* Indicators */}
                  <div style={{ position: 'absolute', bottom: 18, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 10, zIndex: 3 }}>
                    {announcements.map((_, idx) => (
                      <button key={idx} onClick={e => { e.stopPropagation(); goToSlide(idx); }} aria-label={`Go to slide ${idx + 1}`} style={{ width: 16, height: 16, borderRadius: '50%', border: 'none', background: idx === carouselIndex ? '#fff' : 'rgba(255,255,255,0.5)', opacity: idx === carouselIndex ? 1 : 0.7, cursor: 'pointer', transition: 'background 0.2s', boxShadow: idx === carouselIndex ? '0 0 0 2px #08b1c7' : 'none' }} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Evacuation Map Section */}
      <div id="evacuation-map"
        className="responsive-padding section-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '0 0 64px 0',
          padding: '32px 0',
          scrollMarginTop: 88,
          background: 'linear-gradient(135deg, #f8fcfd 0%, #e8f4f8 100%)', 
          borderTop: '1px solid rgba(8, 177, 199, 0.1)',
          borderBottom: '1px solid rgba(8, 177, 199, 0.1)'
        }}>
        <div className="responsive-stack" style={{
          width: '100%',
          maxWidth: 1400,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 48,
          justifyContent: 'center',
          margin: 32,
        }}>
          <div
            ref={evacTextRef}
            className={`evac-text-section${evacTextVisible ? ' visible' : ''}`}
          >
            <div className="responsive-section-title" style={{ 
              fontWeight: '800', 
              fontSize: '2.5rem', 
              color: '#08b1c7', 
              marginBottom: '1em', 
              letterSpacing: 0.5,
              background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Evacuation Map
            </div>
            <div className="responsive-section-subtitle" style={{ 
              fontSize: '1.30rem', 
              color: 'rgb(10, 53, 119)', 
              marginBottom: 24,
              lineHeight: 1.7,
              fontWeight: 400
            }}>
              Gain real-time situational awareness with our interactive Evacuation Map. Instantly locate all official evacuation centers and approved temporary shelters across Legazpi City.
            </div>
            <div className="responsive-section-subtitle" style={{ 
              fontSize: '1.30rem', 
              color: 'rgb(10, 53, 119)',
              lineHeight: 1.6,
              fontWeight: 400
            }}>
              Empower your response and planning with up-to-date capacity and occupancy data—click any marker for critical details and make informed decisions when every second counts.
            </div>
          </div>
          {/* Map */}
          <div className="map-container" style={{ 
            flex: 2, 
            minWidth: 0, 
            width: '100%', 
            position: 'relative', 
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(8, 177, 199, 0.15)',
            background: '#fff'
          }}>
            {isLoaded && !loading ? (
              <GoogleMap
                mapContainerStyle={{
                  ...mapContainerStyle,
                  boxShadow: 'none'
                }}
                center={mapCenter}
                zoom={12}
                options={mapOptions}
              >
                {filteredShelters.map((shelter) => (
                  <OverlayView
                    key={shelter.id}
                    position={{ lat: shelter.latitude, lng: shelter.longitude }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(width, height) => ({ x: -(width / 2), y: -height })}
                  >
                    <div
                      onClick={() => setSelectedShelter(shelter)}
                      style={{
                        background: shelter.shelter_type === 'evacuation_center' 
                          ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' 
                          : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                        color: 'white',
                        padding: '10px 16px',
                        borderRadius: '24px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: '14px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        border: '2px solid rgba(255,255,255,0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                      }}
                    >
                      <div style={{ marginRight: '10px', display: 'flex', alignItems: 'center', fontSize: '18px' }}>
                        {shelter.shelter_type === 'evacuation_center' ? (
                          <FaBuilding style={{ color: '#fff' }} />
                        ) : (
                          <FaTent style={{ color: '#fff' }} />
                        )}
                      </div>
                      <span>{shelter.shelter_name}</span>
                    </div>
                  </OverlayView>
                ))}
                {selectedShelter && (
                  <InfoWindow
                    position={{ lat: selectedShelter.latitude, lng: selectedShelter.longitude }}
                    onCloseClick={() => setSelectedShelter(null)}
                  >
                    <div style={{ padding: '16px', maxWidth: '280px' }}>
                      {/* Image on top */}
                      {(() => {
                        let img = null;
                        if (selectedShelter.shelter_type === 'evacuation_center') {
                          img = selectedShelter.shelter_image1 || selectedShelter.shelter_image2 || selectedShelter.shelter_image3 || selectedShelter.shelter_image4;
                        } else if (selectedShelter.shelter_type === 'temporary_shelter') {
                          img = selectedShelter.house_image1 || selectedShelter.house_image2 || selectedShelter.house_image3 || selectedShelter.house_image4;
                        }
                        return img ? (
                          <img src={img} alt={selectedShelter.shelter_name || selectedShelter.address} style={{ width: '100%', height: '120px', objectFit: 'cover', marginBottom: '12px', borderRadius: 8, display: 'block' }} />
                        ) : null;
                      })()}
                      <h3 style={{ marginBottom: '8px', marginTop: 0, fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>{selectedShelter.shelter_name || selectedShelter.address}</h3>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: '#34495e' }}><strong>Status:</strong> {selectedShelter.status ? selectedShelter.status.charAt(0).toUpperCase() + selectedShelter.status.slice(1) : '-'}</p>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: '#34495e' }}><strong>Total Occupants:</strong> {selectedShelter.shelter_type === 'temporary_shelter' ? ((selectedShelter.members || 0) + (selectedShelter.accepted_occupants || 0)) : (selectedShelter.accepted_occupants || 0)}</p>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: '#34495e' }}><strong>Remaining Capacity:</strong> {selectedShelter.capacity - (selectedShelter.shelter_type === 'temporary_shelter' ? ((selectedShelter.members || 0) + (selectedShelter.accepted_occupants || 0)) : (selectedShelter.accepted_occupants || 0))}</p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <div style={{ 
                height: '500px', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                background: 'linear-gradient(135deg, #f8fcfd 0%, #e8f4f8 100%)', 
                borderRadius: '20px', 
                border: '2px dashed rgba(8, 177, 199, 0.3)',
                width: '100%'
              }}>
                <div style={{ textAlign: 'center', color: '#08b1c7', fontSize: '18px', fontWeight: '500' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗺️</div>
                  Loading Google Maps...
                </div>
              </div>
            )}
            {/* Filter Dropdown */}
            <div className="responsive-filter" style={{ 
              position: 'absolute', 
              top: 20, 
              right: 20, 
              background: 'rgba(255,255,255,0.95)', 
              padding: '12px 16px', 
              borderRadius: '12px', 
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)', 
              zIndex: 1,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <select 
                value={shelterFilter}
                onChange={e => setShelterFilter(e.target.value)}
                style={{ 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  border: '1px solid #e1e8ed', 
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#2c3e50',
                  background: '#fff',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="all">All Shelters</option>
                <option value="evacuation">Evacuation Centers</option>
                <option value="temporary">Temporary Shelters</option>
              </select>
            </div>
            {/* Legend */}
            <div className="responsive-legend" style={{ 
              position: 'absolute', 
              top: 20, 
              left: 20, 
              background: 'rgba(255,255,255,0.95)', 
              padding: '16px', 
              borderRadius: '12px', 
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)', 
              zIndex: 1,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontWeight: '700', marginBottom: '12px', color: '#2c3e50', fontSize: '14px' }}>Legend:</div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', fontSize: '13px', color: '#34495e' }}>
                <FaBuilding style={{ color: '#1976d2', width: 20, height: 20, marginRight: '8px' }} />
                Evacuation Center
              </div>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#34495e' }}>
                <FaTent style={{ color: '#ff9800', width: 20, height: 20, marginRight: '8px' }} />
                Temporary Shelter
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Evacuation Shelter List Section */}
      <div
        id="evacuation-shelter-list"
        className="responsive-padding section-container"
        style={{
          width: '100vw', 
          padding: '2.5rem 2rem', 
          margin: '0 0 64px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: '#fff',
          boxSizing: 'border-box',
          overflowX: 'auto'
        }}
      >
        <div
          className="card-container"
          style={{
            background: '#fff',
            borderRadius: '24px',
            boxShadow: '0 8px 32px rgba(8, 177, 199, 0.12)',
            padding: '2.5em 2em',
            width: '100%',
            maxWidth: 1400,
            // minWidth: 900,
            border: '1px solid rgba(8, 177, 199, 0.08)',
            margin: '0 auto',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '32px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ 
              fontWeight: '800', 
              fontSize: '2rem', 
              color: '#08b1c7',
              background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Evacuation Shelter List
            </div>
            <input
              type="text"
              placeholder="Search by shelter name..."
              value={evacSearch}
              onChange={e => { setEvacSearch(e.target.value); setEvacPage(1); }}
              className="search-input"
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                border: '2px solid #e1e8ed',
                minWidth: 280,
                fontSize: 16,
                fontWeight: 500,
                color: '#2c3e50',
                background: '#f8fcfd',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#08b1c7';
                e.target.style.boxShadow = '0 0 0 3px rgba(8, 177, 199, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e1e8ed';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <div style={{ overflowX: 'auto', width: '100%', borderRadius: '16px', border: '1px solid #e1e8ed' }}>
            <table className="responsive-table" style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              background: 'white', 
              borderRadius: '16px', 
              overflow: 'hidden', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <thead>
                <tr style={{ 
                  background: 'linear-gradient(135deg, #e8f4f8 0%, #d6eef2 100%)', 
                  color: '#08b1c7', 
                  fontWeight: '700', 
                  fontSize: '16px',
                  borderBottom: '2px solid #e1e8ed'
                }}>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Image</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Shelter Name</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Address</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Barangay</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Capacity</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEvacShelters.length > 0 ? paginatedEvacShelters.map(shelter => (
                  <tr key={shelter.id} style={{ 
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fcfd'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '12px', textAlign: 'center' }}>
  {(() => {
    let img = null;
    if (shelter.shelter_type === 'evacuation_center') {
      img = shelter.shelter_image1 || shelter.shelter_image2 || shelter.shelter_image3 || shelter.shelter_image4;
    } else if (shelter.shelter_type === 'temporary_shelter') {
      img = shelter.house_image1 || shelter.house_image2 || shelter.house_image3 || shelter.house_image4;
    }
    return img ? (
      <img src={img} alt={shelter.shelter_name} style={{ 
        width: 70, 
        height: 50, 
        objectFit: 'cover', 
        borderRadius: '8px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e8ed'
      }} />
    ) : (
      <div style={{ 
        width: 70, 
        height: 50, 
        background: 'linear-gradient(135deg, #e8f4f8 0%, #d6eef2 100%)', 
        borderRadius: '8px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        color: '#08b1c7',
        fontSize: '12px',
        fontWeight: '600',
        border: '1px solid #e1e8ed'
      }}>
        No Image
      </div>
    );
  })()}
</td>
                    <td style={{ padding: '12px', fontWeight: '600', color: '#2c3e50' }}>{shelter.shelter_name}</td>
                    <td style={{ padding: '12px', color: '#34495e' }}>{shelter.address || '-'}</td>
                    <td style={{ padding: '12px', color: '#34495e' }}>{shelter.barangay || '-'}</td>
                    <td style={{ padding: '12px', color: '#34495e', fontWeight: '500' }}>{shelter.capacity}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: shelter.status === 'active' ? '#e8f5e8' : '#fff3e0',
                        color: shelter.status === 'active' ? '#2e7d32' : '#f57c00',
                        border: '1px solid',
                        borderColor: shelter.status === 'active' ? '#c8e6c9' : '#ffcc80'
                      }}>
                        {shelter.status ? shelter.status.charAt(0).toUpperCase() + shelter.status.slice(1) : '-'}
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" style={{ 
                      textAlign: 'center', 
                      padding: '48px 0', 
                      color: '#7f8c8d',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏠</div>
                      No shelters found matching your search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalEvacPages > 1 && (
            <div className="pagination-container" style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center', 
              marginTop: '24px', 
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setEvacPage(p => Math.max(1, p - 1))}
                disabled={evacPage === 1}
                style={{ 
                  padding: '8px 16px', 
                  borderRadius: '8px', 
                  border: '2px solid #e1e8ed', 
                  cursor: 'pointer', 
                  opacity: evacPage === 1 ? 0.5 : 1,
                  background: '#fff',
                  color: '#2c3e50',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (evacPage !== 1) {
                    e.target.style.borderColor = '#08b1c7';
                    e.target.style.color = '#08b1c7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (evacPage !== 1) {
                    e.target.style.borderColor = '#e1e8ed';
                    e.target.style.color = '#2c3e50';
                  }
                }}
              >
                Previous
              </button>
              <div style={{ display: 'flex', gap: '4px' }}>
                {[...Array(totalEvacPages).keys()].map(num => (
                  <button
                    key={num + 1}
                    onClick={() => setEvacPage(num + 1)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '2px solid #e1e8ed',
                      cursor: 'pointer',
                      background: evacPage === num + 1 ? '#08b1c7' : '#fff',
                      color: evacPage === num + 1 ? '#fff' : '#2c3e50',
                      fontWeight: evacPage === num + 1 ? '700' : '600',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      minWidth: '40px'
                    }}
                    onMouseEnter={(e) => {
                      if (evacPage !== num + 1) {
                        e.target.style.borderColor = '#08b1c7';
                        e.target.style.color = '#08b1c7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (evacPage !== num + 1) {
                        e.target.style.borderColor = '#e1e8ed';
                        e.target.style.color = '#2c3e50';
                      }
                    }}
                  >
                    {num + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setEvacPage(p => Math.min(totalEvacPages, p + 1))}
                disabled={evacPage === totalEvacPages}
                style={{ 
                  padding: '8px 16px', 
                  borderRadius: '8px', 
                  border: '2px solid #e1e8ed', 
                  cursor: 'pointer', 
                  opacity: evacPage === totalEvacPages ? 0.5 : 1,
                  background: '#fff',
                  color: '#2c3e50',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (evacPage !== totalEvacPages) {
                    e.target.style.borderColor = '#08b1c7';
                    e.target.style.color = '#08b1c7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (evacPage !== totalEvacPages) {
                    e.target.style.borderColor = '#e1e8ed';
                    e.target.style.color = '#2c3e50';
                  }
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Reports Map Section */}
      <div id="reports-map"
        className="responsive-padding section-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '0 0 64px 0',
          scrollMarginTop: 88,
          background: 'linear-gradient(135deg, #f8fcfd 0%, #e8f4f8 100%)', 
          padding: '48px 0px',
          borderTop: '1px solid rgba(8, 177, 199, 0.1)',
          borderBottom: '1px solid rgba(8, 177, 199, 0.1)'
        }}>
        <div className="responsive-stack" style={{
          width: '100%',
          maxWidth: 1400,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 48,
          margin: 32
        }}>
          <div
            ref={reportsTextRef}
            className={`reports-text-section${reportsTextVisible ? ' visible' : ''}`}
          >
            <div className="responsive-section-title" style={{ 
              fontWeight: '800', 
              fontSize: '2.5rem', 
              color: '#08b1c7', 
              marginBottom: '1em', 
              letterSpacing: 0.5,
              background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Reports Map
            </div>
            <div className="responsive-section-subtitle" style={{ 
              fontSize: '1.30rem', 
              color: 'rgb(10, 53, 119)', 
              marginBottom: 24,
              lineHeight: 1.7,
              fontWeight: 400
            }} >
              Visualize emergencies as they unfold with the dynamic Reports Map. Track incident reports citywide, assess severity and status at a glance.
            </div>
            <div className="responsive-section-subtitle" style={{ 
              fontSize: '1.30rem', 
              color: 'rgb(10, 53, 119)',
              lineHeight: 1.6,
              fontWeight: 400
            }}>
              Stay ahead of developing situations and coordinate effective responses with the power of real-time data at your fingertips.
            </div>
          </div>
          {/* Map */}
          <div className="map-container" style={{ 
            flex: 2, 
            minWidth: 0, 
            width: '100%', 
            position: 'relative', 
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(8, 177, 199, 0.15)',
            background: '#fff'
          }}>
            {isLoaded && !loading ? (
              <GoogleMap
                mapContainerStyle={{
                  ...mapContainerStyle,
                  boxShadow: 'none'
                }}
                center={reportsMapCenter}
                zoom={12}
                options={mapOptions}
                onLoad={setReportsMap}
              >
                {selectedIncident && (
                  <InfoWindow
                    position={{ lat: selectedIncident.location_coords.latitude, lng: selectedIncident.location_coords.longitude }}
                    onCloseClick={() => setSelectedIncident(null)}
                  >
                    <div style={{ padding: '16px', maxWidth: '280px' }}>
                      {/* You can add incident details here */}
                      <h3 style={{ marginBottom: '8px', marginTop: 0, fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>
                        {selectedIncident.type ? selectedIncident.type.charAt(0).toUpperCase() + selectedIncident.type.slice(1) : '-'}
                      </h3>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: '#34495e' }}>
                        <strong>Location:</strong> {selectedIncident.location || '-'}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: '#34495e' }}>
                        <strong>Severity:</strong> {selectedIncident.severity ? selectedIncident.severity.charAt(0).toUpperCase() + selectedIncident.severity.slice(1) : '-'}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: '#34495e' }}>
                        <strong>Status:</strong> {selectedIncident.status ? selectedIncident.status.replace('_', ' ').charAt(0).toUpperCase() + selectedIncident.status.replace('_', ' ').slice(1) : '-'}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: '#34495e' }}>
                        <strong>Details:</strong> {selectedIncident.details || '-'}
                      </p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <div style={{ 
                height: '500px', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                background: 'linear-gradient(135deg, #f8fcfd 0%, #e8f4f8 100%)', 
                borderRadius: '20px', 
                border: '2px dashed rgba(8, 177, 199, 0.3)',
                width: '100%'
              }}>
                <div style={{ textAlign: 'center', color: '#08b1c7', fontSize: '18px', fontWeight: '500' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗺️</div>
                  Loading Google Maps...
                </div>
              </div>
            )}
            {/* Filter Dropdown */}
            <div className="responsive-filter" style={{ 
              position: 'absolute', 
              top: 20, 
              right: 20, 
              background: 'rgba(255,255,255,0.95)', 
              padding: '12px 16px', 
              borderRadius: '12px', 
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)', 
              zIndex: 1,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <select 
                value={reportTypeFilter}
                onChange={e => setReportTypeFilter(e.target.value)}
                style={{ 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  border: '1px solid #e1e8ed', 
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#2c3e50',
                  background: '#fff',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="all">All Types</option>
                <option value="fire">Fire</option>
                <option value="flood">Flood</option>
                <option value="medical">Medical</option>
                <option value="rescue">Rescue</option>
                <option value="others">Others</option>
              </select>
            </div>
            {/* Legend */}
            <div className="responsive-legend" style={{ 
              position: 'absolute', 
              top: 20, 
              left: 20, 
              background: 'rgba(255,255,255,0.95)', 
              padding: '16px', 
              borderRadius: '12px', 
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)', 
              zIndex: 1,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontWeight: '700', marginBottom: '12px', color: '#2c3e50', fontSize: '14px' }}>Legend:</div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', fontSize: '13px', color: '#34495e' }}>
                <FaFire style={{ color: '#e65100', width: 20, height: 20, marginRight: '8px' }} />
                Fire
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', fontSize: '13px', color: '#34495e' }}>
                <FaHouseFloodWater style={{ color: '#1976d2', width: 20, height: 20, marginRight: '8px' }} />
                Flood
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', fontSize: '13px', color: '#34495e' }}>
                <FaHeart style={{ color: '#d32f2f', width: 20, height: 20, marginRight: '8px' }}/>
                Medical
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', fontSize: '13px', color: '#34495e' }}>
                <FaTruck style={{ color: '#388e3c', width: 20, height: 20, marginRight: '8px' }} />
                Rescue
              </div>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#34495e' }}>
                <FaExclamation style={{ color: '#fbc02d', width: 20, height: 20, marginRight: '8px' }} />
                Others
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Incident Reports List Section */}
      <div id="incident-reports-list"
        className="responsive-padding section-container"
        style={{
          width: '100vw', 
          margin: '0 0 64px 0',
          padding: '2.5rem 2rem', 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: '#fff',
          boxSizing: 'border-box',
          overflowX: 'auto'
        }}>
        <div
          className="card-container"
          style={{
            background: '#fff',
            borderRadius: '24px',
            boxShadow: '0 8px 32px rgba(8, 177, 199, 0.12)',
            padding: '2.5em 2em',
            width: '100%',
            maxWidth: 1400,
            // minWidth: 900, 
            border: '1px solid rgba(8, 177, 199, 0.08)',
            margin: '0 auto',
            boxSizing: 'border-box',
          }}
        >
          <div className="list-header" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '32px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ 
              fontWeight: '800', 
              fontSize: '2rem', 
              color: '#08b1c7',
              background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Incident Reports List
            </div>
            <input
              type="text"
              placeholder="Search by location..."
              value={reportSearch}
              onChange={e => { setReportSearch(e.target.value); setReportPage(1); }}
              className="search-input"
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                border: '2px solid #e1e8ed',
                minWidth: 280,
                fontSize: 16,
                fontWeight: 500,
                color: '#2c3e50',
                background: '#f8fcfd',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#08b1c7';
                e.target.style.boxShadow = '0 0 0 3px rgba(8, 177, 199, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e1e8ed';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <div style={{ overflowX: 'auto', width: '100%', borderRadius: '16px', border: '1px solid #e1e8ed' }}>
            <table className="responsive-table" style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              background: 'white', 
              borderRadius: '16px', 
              overflow: 'hidden', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <thead>
                <tr style={{ 
                  background: 'linear-gradient(135deg, #e8f4f8 0%, #d6eef2 100%)', 
                  color: '#08b1c7', 
                  fontWeight: '700', 
                  fontSize: '16px',
                  borderBottom: '2px solid #e1e8ed'
                }}>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Image</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Location</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Severity</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {paginatedIncidents.length > 0 ? paginatedIncidents.map(incident => (
                  <tr key={incident.id} style={{ 
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: expandedRows.has(incident.id) ? '#f8fcfd' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedRows.has(incident.id)) {
                      e.target.style.backgroundColor = '#f8fcfd';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedRows.has(incident.id)) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  >
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {incident.image_url ? (
                        <img src={incident.image_url} alt={incident.type} style={{ 
                          width: 70, 
                          height: 50, 
                          objectFit: 'cover', 
                          borderRadius: '8px', 
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          border: '1px solid #e1e8ed'
                        }} />
                      ) : (
                        <div style={{ 
                          width: 70, 
                          height: 50, 
                          background: 'linear-gradient(135deg, #e8f4f8 0%, #d6eef2 100%)', 
                          borderRadius: '8px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          color: '#08b1c7',
                          fontSize: '12px',
                          fontWeight: '600',
                          border: '1px solid #e1e8ed'
                        }}>
                          No Image
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontWeight: '600', color: '#2c3e50' }}>
                      {incident.type ? incident.type.charAt(0).toUpperCase() + incident.type.slice(1) : '-'}
                    </td>
                    <td style={{ padding: '12px', color: '#34495e' }}>{incident.location || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: incident.severity === 'high' ? '#ffebee' : 
                                   incident.severity === 'medium' ? '#fff3e0' : '#e8f5e8',
                                               color: incident.severity === 'high' ? '#c62828' : 
                               incident.severity === 'medium' ? '#f57c00' : '#2e7d32',
                        border: '1px solid',
                        borderColor: incident.severity === 'high' ? '#ffcdd2' : 
                                    incident.severity === 'medium' ? '#ffcc80' : '#c8e6c9'
                      }}>
                        {incident.severity ? incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1) : '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: incident.status === 'resolved' ? '#e8f5e8' : 
                                   incident.status === 'in_progress' ? '#fff3e0' : '#ffebee',
                        color: incident.status === 'resolved' ? '#2e7d32' : 
                               incident.status === 'in_progress' ? '#f57c00' : '#c62828',
                        border: '1px solid',
                        borderColor: incident.status === 'resolved' ? '#c8e6c9' : 
                                    incident.status === 'in_progress' ? '#ffcc80' : '#ffcdd2'
                      }}>
                        {incident.status ? incident.status.replace('_', ' ').charAt(0).toUpperCase() + incident.status.replace('_', ' ').slice(1) : '-'}
                      </span>
                    </td>
                    <td className="details-cell" style={{ 
                      padding: '12px', 
                      maxWidth: 200, 
                      minWidth: 150,
                      color: '#34495e',
                      verticalAlign: 'top'
                    }}>
                      {shouldTruncate(incident.details) ? (
                        <div>
                          <div className="details-text" style={{ 
                            whiteSpace: expandedRows.has(incident.id) ? 'normal' : 'nowrap',
                            overflow: expandedRows.has(incident.id) ? 'visible' : 'hidden',
                            textOverflow: expandedRows.has(incident.id) ? 'clip' : 'ellipsis',
                            lineHeight: '1.5',
                            wordBreak: 'break-word'
                          }}>
                            {expandedRows.has(incident.id) ? incident.details : truncateText(incident.details)}
                          </div>
                          <button
                            className="show-more-btn"
                            onClick={() => toggleRowExpansion(incident.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#08b1c7',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              padding: '4px 0',
                              marginTop: '4px',
                              textDecoration: 'underline',
                              transition: 'color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#0d47a1'}
                            onMouseLeave={(e) => e.target.style.color = '#08b1c7'}
                          >
                            {expandedRows.has(incident.id) ? 'Show Less' : 'Show More'}
                          </button>
                        </div>
                      ) : (
                        <div className="details-text" style={{ 
                          lineHeight: '1.5',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal'
                        }}>
                          {incident.details}
                        </div>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" style={{ 
                      textAlign: 'center', 
                      padding: '48px 0', 
                      color: '#7f8c8d',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚨</div>
                      No incidents found matching your search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalIncidentPages > 1 && (
            <div className="pagination-container" style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center', 
              marginTop: '24px', 
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setReportPage(p => Math.max(1, p - 1))}
                disabled={reportPage === 1}
                style={{ 
                  padding: '8px 16px', 
                  borderRadius: '8px', 
                  border: '2px solid #e1e8ed', 
                  cursor: 'pointer', 
                  opacity: reportPage === 1 ? 0.5 : 1,
                  background: '#fff',
                  color: '#2c3e50',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (reportPage !== 1) {
                    e.target.style.borderColor = '#08b1c7';
                    e.target.style.color = '#08b1c7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (reportPage !== 1) {
                    e.target.style.borderColor = '#e1e8ed';
                    e.target.style.color = '#2c3e50';
                  }
                }}
              >
                Previous
              </button>
              <div style={{ display: 'flex', gap: '4px' }}>
                {[...Array(totalIncidentPages).keys()].map(num => (
                  <button
                    key={num + 1}
                    onClick={() => setReportPage(num + 1)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '2px solid #e1e8ed',
                      cursor: 'pointer',
                      background: reportPage === num + 1 ? '#08b1c7' : '#fff',
                      color: reportPage === num + 1 ? '#fff' : '#2c3e50',
                      fontWeight: reportPage === num + 1 ? '700' : '600',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      minWidth: '40px'
                    }}
                    onMouseEnter={(e) => {
                      if (reportPage !== num + 1) {
                        e.target.style.borderColor = '#08b1c7';
                        e.target.style.color = '#08b1c7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (reportPage !== num + 1) {
                        e.target.style.borderColor = '#e1e8ed';
                        e.target.style.color = '#2c3e50';
                      }
                    }}
                  >
                    {num + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setReportPage(p => Math.min(totalIncidentPages, p + 1))}
                disabled={reportPage === totalIncidentPages}
                style={{ 
                  padding: '8px 16px', 
                  borderRadius: '8px', 
                  border: '2px solid #e1e8ed', 
                  cursor: 'pointer', 
                  opacity: reportPage === totalIncidentPages ? 0.5 : 1,
                  background: '#fff',
                  color: '#2c3e50',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (reportPage !== totalIncidentPages) {
                    e.target.style.borderColor = '#08b1c7';
                    e.target.style.color = '#08b1c7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (reportPage !== totalIncidentPages) {
                    e.target.style.borderColor = '#e1e8ed';
                    e.target.style.color = '#2c3e50';
                  }
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* About Us Section */}
      <div id="about"
        className="responsive-padding section-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          margin: '0 0 64px 0',
          padding: '48px 0',
          scrollMarginTop: 88,
          background: 'linear-gradient(135deg, #f8fcfd 0%, #e8f4f8 100%)', 
          borderTop: '1px solid rgba(8, 177, 199, 0.1)',
          borderBottom: '1px solid rgba(8, 177, 199, 0.1)'
        }}>
        
        {/* About Us Title and Description */}
        <div 
          ref={aboutTextRef}
          style={{
            width: '100%',
            maxWidth: 1400,
            textAlign: 'center',
            marginBottom: '4rem',
            padding: '0 32px',
            opacity: aboutTextVisible ? 1 : 0,
            transform: aboutTextVisible ? 'translateY(0)' : 'translateY(60px)',
            transition: 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <div className="responsive-section-title" style={{ 
            fontWeight: '800', 
            fontSize: '2.5rem', 
            color: '#08b1c7', 
            marginBottom: '2rem', 
            letterSpacing: 0.5,
            background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            About Us
          </div>
          <div className="responsive-section-subtitle" style={{ 
            fontSize: '1.30rem', 
            color: 'rgb(10, 53, 119)', 
            marginBottom: 24,
            lineHeight: 1.7,
            fontWeight: 400,
            maxWidth: 800,
            margin: '0 auto 24px auto'
          }}>
            The Legazpi City Disaster Risk Reduction and Management Office (DRRMO) is dedicated to building a resilient and safe community. Our mission is to protect the people of Legazpi through proactive disaster monitoring, efficient emergency response, and public empowerment.
          </div>
          <div className="responsive-section-subtitle" style={{ 
            fontSize: '1.30rem', 
            color: 'rgb(10, 53, 119)', 
            lineHeight: 1.6,
            fontWeight: 400,
            maxWidth: 800,
            margin: '0 auto'
          }}>
            By leveraging modern technology, we enhance real-time awareness and coordination to ensure the city is prepared for any emergency.
          </div>
        </div>

        {/* Mission & Vision Cards */}
        <div className="mission-vision-cards" style={{
          display: 'flex',
          gap: 32,
          margin: '0 32px 64px 32px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          width: '100%',
          maxWidth: 1400
        }}>
          {/* Mission Card */}
          <div className="mission-vision-card" style={{
            background: '#fff',
            borderRadius: '24px',
            padding: '40px 32px',
            boxShadow: '0 8px 32px rgba(8, 177, 199, 0.12)',
            border: '1px solid rgba(8, 177, 199, 0.08)',
            flex: 1,
            minWidth: 300,
            maxWidth: 400,
            textAlign: 'center',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-8px)';
            e.target.style.boxShadow = '0 16px 48px rgba(8, 177, 199, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 8px 32px rgba(8, 177, 199, 0.12)';
          }}>
            <div style={{ 
              fontSize: '64px', 
              marginBottom: '24px',
              filter: 'drop-shadow(0 4px 8px rgba(8, 177, 199, 0.2))'
            }}>
              🎯
            </div>
            <div style={{ 
              fontWeight: '800', 
              fontSize: '1.5rem', 
              color: '#08b1c7', 
              marginBottom: '16px',
              background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Our Mission
            </div>
            <div style={{ 
              fontSize: '1rem', 
              color: '#34495e', 
              lineHeight: 1.6,
              fontWeight: 400
            }}>
              To provide a comprehensive disaster management platform that enhances community safety and resilience through real-time monitoring, rapid response coordination, and community empowerment.
            </div>
          </div>

          {/* Vision Card */}
          <div className="mission-vision-card" style={{
            background: '#fff',
            borderRadius: '24px',
            padding: '40px 32px',
            boxShadow: '0 8px 32px rgba(8, 177, 199, 0.12)',
            border: '1px solid rgba(8, 177, 199, 0.08)',
            flex: 1,
            minWidth: 300,
            maxWidth: 400,
            textAlign: 'center',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-8px)';
            e.target.style.boxShadow = '0 16px 48px rgba(8, 177, 199, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 8px 32px rgba(8, 177, 199, 0.12)';
          }}>
            <div style={{ 
              fontSize: '64px', 
              marginBottom: '24px',
              filter: 'drop-shadow(0 4px 8px rgba(8, 177, 199, 0.2))'
            }}>
              🌟
            </div>
            <div style={{ 
              fontWeight: '800', 
              fontSize: '1.5rem', 
              color: '#08b1c7', 
              marginBottom: '16px',
              background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Our Vision
            </div>
            <div style={{ 
              fontSize: '1rem', 
              color: '#34495e', 
              lineHeight: 1.6,
              fontWeight: 400
            }}>
              To create safer, more resilient communities where technology bridges the gap between emergency response and community needs, ensuring no one is left behind during disasters.
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="features-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 24,
          width: '100%',
          maxWidth: 1400,
          marginBottom: 64,
          padding: '0 32px'
        }}>
          {/* Real-time Monitoring */}
          <div className="feature-card" style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '32px 24px',
            boxShadow: '0 4px 20px rgba(8, 177, 199, 0.1)',
            border: '1px solid rgba(8, 177, 199, 0.06)',
            textAlign: 'center',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-4px)';
            e.target.style.boxShadow = '0 8px 32px rgba(8, 177, 199, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 20px rgba(8, 177, 199, 0.1)';
          }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              filter: 'drop-shadow(0 2px 4px rgba(8, 177, 199, 0.2))'
            }}>
              📡
            </div>
            <div style={{ 
              fontWeight: '700', 
              fontSize: '1.25rem', 
              color: '#08b1c7', 
              marginBottom: '12px'
            }}>
              Real-time Monitoring
            </div>
            <div style={{ 
              fontSize: '0.95rem', 
              color: '#34495e', 
              lineHeight: 1.5,
              fontWeight: 400
            }}>
              Live tracking of incidents, evacuation centers, and emergency responses across Legazpi City.
            </div>
          </div>

          {/* Community Empowerment */}
          <div className="feature-card" style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '32px 24px',
            boxShadow: '0 4px 20px rgba(8, 177, 199, 0.1)',
            border: '1px solid rgba(8, 177, 199, 0.06)',
            textAlign: 'center',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-4px)';
            e.target.style.boxShadow = '0 8px 32px rgba(8, 177, 199, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 20px rgba(8, 177, 199, 0.1)';
          }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              filter: 'drop-shadow(0 2px 4px rgba(8, 177, 199, 0.2))'
            }}>
              🤝
            </div>
            <div style={{ 
              fontWeight: '700', 
              fontSize: '1.25rem', 
              color: '#08b1c7', 
              marginBottom: '12px'
            }}>
              Community Empowerment
            </div>
            <div style={{ 
              fontSize: '0.95rem', 
              color: '#34495e', 
              lineHeight: 1.5,
              fontWeight: 400
            }}>
              Enabling citizens to report incidents, access information, and contribute to community safety.
            </div>
          </div>

          {/* Technology Innovation */}
          <div className="feature-card" style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '32px 24px',
            boxShadow: '0 4px 20px rgba(8, 177, 199, 0.1)',
            border: '1px solid rgba(8, 177, 199, 0.06)',
            textAlign: 'center',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-4px)';
            e.target.style.boxShadow = '0 8px 32px rgba(8, 177, 199, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 20px rgba(8, 177, 199, 0.1)';
          }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              filter: 'drop-shadow(0 2px 4px rgba(8, 177, 199, 0.2))'
            }}>
              💻
            </div>
            <div style={{ 
              fontWeight: '700', 
              fontSize: '1.25rem',  
              color: '#08b1c7', 
              marginBottom: '12px'
            }}>
              Technology Innovation
            </div>
            <div style={{ 
              fontSize: '0.95rem', 
              color: '#34495e', 
              lineHeight: 1.5,
              fontWeight: 400
            }}>
              Modern web and mobile technologies designed for reliability and ease of use during emergencies.
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(8, 177, 199, 0.1) 0%, rgba(13, 71, 161, 0.1) 100%)',
          borderRadius: '24px',
          padding: '48px 32px',
          textAlign: 'center',
          border: '2px solid rgba(8, 177, 199, 0.2)',
          maxWidth: 800,
          width: '100%',
          margin: '0 32px'
        }}>
          <div style={{ 
            fontSize: '64px', 
            marginBottom: '24px',
            filter: 'drop-shadow(0 4px 8px rgba(8, 177, 199, 0.2))'
          }}>
            🚀
          </div>
          <div style={{ 
            fontWeight: '800', 
            fontSize: '2rem', 
            color: '#08b1c7', 
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Join Our Mission
          </div>
          <div style={{ 
            fontSize: '1.125rem', 
            color: '#2c3e50', 
            lineHeight: 1.6,
            fontWeight: 400,
            marginBottom: '32px'
          }}>
            Together, we can build a safer, more resilient Legazpi City. Download our mobile app and become part of the solution.
          </div>
          <a
            href="https://expo.dev/accounts/odysseum/projects/gabaysakuna/builds/3f5b3ab6-62ab-4478-a222-327597b817b2?fbclid=IwY2xjawLP7BdleHRuA2FlbQIxMQABHjZjNjI4x_ydH2ewxh1fml1IQsXGYCbmFjETaXB2bqVvRfgJoRVu6q_bQOCP_aem_mwuHgmQkeng3wZsafny6bQ"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button"
            style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #08b1c7 0%, #0d47a1 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '16px',
              padding: '16px 32px',
              fontSize: '1.125rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(8, 177, 199, 0.3)',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 0,
              marginTop: 8,
              textAlign: 'center',
              textDecoration: 'none'
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 32px rgba(8, 177, 199, 0.4)';
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 24px rgba(8, 177, 199, 0.3)';
            }}
          >
            Download App
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer" style={{
        background: '#f8fcfd',
        borderTop: '3px solid #08b1c7',
        marginTop: 32,
        padding: '32px 0 0 0',
        fontFamily: 'inherit',
        boxShadow: '0 -2px 12px rgba(8, 177, 199, 0.1)'
      }}>
        <div className="footer-content" style={{
          maxWidth: 1400,
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '0 4vw 24px 4vw'
        }}>
          {/* Left: Logo and description */}
          <div className="footer-section" style={{ flex: 1, minWidth: 260, marginBottom: 24 }}>
            <div className="footer-logo" style={{ display: 'flex', alignItems: 'center', marginBottom: 16, justifyContent: 'center' }}>
              <img src="/GabaySakunaLogo.svg" alt="GabaySakuna Logo" style={{ height: 40, marginRight: 14 }} />
              <span style={{ fontWeight: 'bold', fontSize: 24, color: '#08b1c7' }}>GabaySakuna</span>
            </div>
            <div style={{ color: '#2c3e50', fontSize: 16, lineHeight: 1.6, fontWeight: 400, textAlign: 'center', margin: '0 auto', width: '100%', display: 'block' }}>
              Empowering Legazpi City with real-time disaster monitoring, response, and community resilience. Building safer communities through technology and collaboration.
            </div>
          </div>
          
          {/* Center: Contact Us */}
          <div className="footer-section" style={{ flex: 1, minWidth: 260, marginBottom: 24 }}>
            <div style={{ fontWeight: 'bold', fontSize: 20, color: '#08b1c7', marginBottom: 16, textAlign: 'center' }}>Contact Us</div>
            <div className="footer-contact-item" style={{ color: '#2c3e50', fontSize: 16, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ marginRight: 12, fontSize: 18 }}>📍</span> 
              <span>Legazpi City, Albay</span>
            </div>
            <div className="footer-contact-item" style={{ color: '#2c3e50', fontSize: 16, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ marginRight: 12, fontSize: 18 }}>📞</span> 
              <span>(+63) 912 345 6789</span>
            </div>
            <div className="footer-contact-item" style={{ color: '#2c3e50', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ marginRight: 12, fontSize: 18 }}>✉️</span> 
              <span>gabaysakuna@gmail.com</span>
            </div>
          </div>
          
          {/* Right: Quick Links */}
          <div className="footer-section" style={{ flex: 1, minWidth: 260, marginBottom: 24 }}>
            <div style={{ fontWeight: 'bold', fontSize: 20, color: '#08b1c7', marginBottom: 16, textAlign: 'center' }}>Quick Links</div>
            <div className="footer-links" style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
              <a 
                href="#evacuation-map" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('evacuation-map')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{ 
                  color: '#2c3e50', 
                  fontSize: 16, 
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => e.target.style.color = '#08b1c7'}
                onMouseLeave={(e) => e.target.style.color = '#2c3e50'}
              >
                Evacuation Map
              </a>
              <a 
                href="#reports-map" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('reports-map')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{ 
                  color: '#2c3e50', 
                  fontSize: 16, 
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => e.target.style.color = '#08b1c7'}
                onMouseLeave={(e) => e.target.style.color = '#2c3e50'}
              >
                Reports Map
              </a>
              <a 
                href="#about" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{ 
                  color: '#2c3e50', 
                  fontSize: 16, 
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => e.target.style.color = '#08b1c7'}
                onMouseLeave={(e) => e.target.style.color = '#2c3e50'}
              >
                About Us
              </a>
            </div>
          </div>
        </div>
        <div className="footer-copyright" style={{
          borderTop: '2px solid #b2dbe6',
          textAlign: 'center',
          color: '#34495e',
          fontSize: 16,
          padding: '16px 4vw',
          background: '#f8fcfd',
          fontWeight: 500
        }}>
          © {new Date().getFullYear()} GabaySakuna. All rights reserved.
        </div>
      </footer>
    </div>
    </>
  );
}

export default Homepage;